import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Download, ChevronDown, ChevronUp, FileText, ExternalLink } from "lucide-react";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  imported: "bg-emerald-50 text-emerald-600",
  staged: "bg-amber-50 text-amber-600",
  ready: "bg-blue-50 text-blue-600",
  duplicate: "bg-orange-50 text-orange-600",
  invalid: "bg-red-50 text-red-600",
  failed: "bg-red-50 text-red-600",
};

const Badge = ({ status }) => (
  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[status] || "bg-slate-100 text-slate-500"}`}>
    {status}
  </span>
);

const BatchRow = ({ batch }) => {
  const [open, setOpen] = useState(false);
  const results = batch.results || [];
  const files = batch.files || [];
  const summary = batch.status === "imported"
    ? `${results.filter((r) => r.status === "imported").length} imported · ${results.filter((r) => r.status.startsWith("skipped")).length} skipped · ${results.filter((r) => r.status === "failed").length} failed`
    : `${files.filter((f) => f.status === "ready").length} ready · ${files.filter((f) => f.status === "duplicate").length} duplicate · ${files.filter((f) => f.status === "invalid").length} invalid`;

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/import-batches/${batch.id}/report.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import-report-${batch.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the CSV report");
    }
  };

  const rows = batch.status === "imported"
    ? results.map((r) => ({ ...r, extra: files.find((f) => f.filename === r.filename) || {} }))
    : files.map((f) => ({ ...f, extra: f }));

  return (
    <div className="p-4" data-testid={`import-batch-${batch.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="font-medium text-slate-900 text-sm">Batch {batch.id.slice(0, 8)}</span>
            <Badge status={batch.status} />
            {batch.profile_id && (
              <span className="text-xs text-slate-400">profile: {batch.profile_id}</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {batch.created_at ? new Date(batch.created_at).toLocaleString() : ""} · by {batch.created_by_email} · {files.length} file{files.length !== 1 ? "s" : ""} · {summary}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={downloadCsv} data-testid={`batch-csv-btn-${batch.id}`}>
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpen(!open)} data-testid={`batch-toggle-${batch.id}`}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-3 border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium text-slate-600">File</th>
                <th className="px-3 py-1.5 text-left font-medium text-slate-600">Status</th>
                <th className="px-3 py-1.5 text-left font-medium text-slate-600">Title</th>
                <th className="px-3 py-1.5 text-left font-medium text-slate-600">Collection</th>
                <th className="px-3 py-1.5 text-left font-medium text-slate-600">Details</th>
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="px-3 py-1.5 text-slate-700 max-w-[180px] truncate">{r.filename}</td>
                  <td className="px-3 py-1.5"><Badge status={r.status?.replace("skipped_", "")} /></td>
                  <td className="px-3 py-1.5 text-slate-600 max-w-[220px] truncate">{r.title || r.extra?.detected_title || "—"}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.collection || r.extra?.proposed_collection || "—"}</td>
                  <td className="px-3 py-1.5 text-slate-500 max-w-[260px] truncate">
                    {(r.reasons || []).map((x) => `${x.type}${x.detail ? `: ${x.detail}` : ""}`).join("; ") || (r.extra?.doi ? `DOI ${r.extra.doi}` : "—")}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {r.item_id && (
                      <button onClick={() => window.open(`/items/${r.item_id}`, "_blank")} className="text-orange-600 hover:text-orange-700" title="View item" data-testid={`view-imported-item-${r.item_id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AdminImportHistory({ user, setUser }) {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/admin/import-batches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBatches(resp.data.batches || []);
      } catch {
        toast.error("Could not load import history");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold" style={{ fontFamily: "Fraunces, serif" }}>Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate("/")} variant="ghost" className="text-white hover:text-white hover:bg-slate-800">Public Site</Button>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:text-white hover:bg-slate-800"><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="imports" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Fraunces, serif" }}>Import History</h2>
          <p className="text-sm text-slate-500 mt-1">
            Every ChatGPT Library Importer batch — staging reports, duplicates, and imported items
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : batches.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center" data-testid="imports-empty">
            <p className="text-slate-500 text-sm">
              No imports yet. Connect ChatGPT to your library importer (see MCP_CONNECTOR_GUIDE.md)
              and batches will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-200" data-testid="imports-list">
            {batches.map((b) => <BatchRow key={b.id} batch={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}
